import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { FormTypes } from '../../../../account-info/models/form-models';
import { PensionEnrollmentProps } from '../../../models/arc-epi-props';
import { PensionEnrollmentFormValues, pensionEnrollmentValidationSchema } from '../models/pension-enrollment-form-model';
import { BillPayerOption, PensionEnrollmentContributionType, PensionEnrollmentInfo, PensionEnrollmentPlan, PensionEnrollmentStep } from '../models/pension-enrollment-model';
import { PensionEnrollmentPlanDetails } from './pension-enrollment-plan-details';
import { PensionEnrollmentPlanSelection } from './pension-enrollment-plan-selection';
import { PensionEnrollmentVerifyMarital } from './pension-enrollment-verify-marital';

interface Props {
	currentStep: PensionEnrollmentStep;
	pensionInfo: PensionEnrollmentInfo;
	plansAvailable: boolean;
	labelSettings: LabelSettingProps;
	pensionEnrollmentProps: PensionEnrollmentProps;
	cancelAction: (formikProps: FormikProps<PensionEnrollmentFormValues>) => any;
	onSubmit: (values: PensionEnrollmentFormValues, formikHelpers: FormikHelpers<PensionEnrollmentFormValues>) => any;
}

function getPreviousBillPayer(currentPlan: PensionEnrollmentPlan) {

	if (!currentPlan) {
		return '';
	}

	return currentPlan.employer_client_number === currentPlan.bill_payer_employer_client_number ? BillPayerOption.Employer : BillPayerOption.Diocese;
}

export function PensionEnrollmentForm(props: Props) {

	const { currentStep, pensionEnrollmentProps, pensionInfo, cancelAction, onSubmit, labelSettings, plansAvailable } = props;

	const preventButtonDisableValidation = !plansAvailable || (currentStep !== PensionEnrollmentStep.Detail && currentStep !== PensionEnrollmentStep.Landing);

	const apiErrors = useApiResponseError(FormTypes.PENSION_ENROLLMENT);


	const renderFormContent = (formProps: FormikProps<PensionEnrollmentFormValues>) => {
		if (currentStep === PensionEnrollmentStep.Marital) {
			return <PensionEnrollmentVerifyMarital pensionEnrollmentProps={pensionEnrollmentProps} />;
		}

		if (currentStep === PensionEnrollmentStep.Landing) {
			return <PensionEnrollmentPlanSelection
				formProps={formProps}
				pensionEnrollmentProps={pensionEnrollmentProps}
				pensionInfo={pensionInfo} />;
		}

		return <PensionEnrollmentPlanDetails
			formProps={formProps}
			pensionEnrollmentProps={pensionEnrollmentProps}
			pensionInfo={pensionInfo} />;
	};

	const buttonLabels = (() => {

		if (!plansAvailable) {
			return ['', pensionEnrollmentProps?.mapHomeButtonText || 'MAP Home'];
		}

		switch (currentStep) {
			case PensionEnrollmentStep.Marital:
				return [pensionEnrollmentProps?.maritalStatusNotCorrectLinkText, pensionEnrollmentProps?.maritalStatusCorrectLinkText];
			case PensionEnrollmentStep.Landing:
				return [pensionEnrollmentProps?.cancelButtonText, pensionEnrollmentProps?.continueToPlanDetailsButtonText];
			case PensionEnrollmentStep.Detail:
				return [pensionEnrollmentProps?.cancelButtonText, pensionEnrollmentProps?.enrollButtonText];
			default:
				return ['', ''];
		}
	})();

	const submitDisabled = (formProps: FormikProps<PensionEnrollmentFormValues>) => {
		if (!plansAvailable) return false;

		switch (currentStep) {
			case PensionEnrollmentStep.Landing:
				return !formProps.values.plan;
			case PensionEnrollmentStep.Detail:
				return !formProps.values.plan || !formProps.values.agreement;
			default:
				return false;
		}
	};

	const initialValues: PensionEnrollmentFormValues = {
		plan: '',
		bill_payer: getPreviousBillPayer(pensionInfo?.enrolled_plans[0]),
		effective_date_month: null,
		effective_date_year: null,
		contribution_type: PensionEnrollmentContributionType.Percentage,
		contribution_amount: '0.00',
		agreement: false,
	};

	const validationSchema = pensionEnrollmentValidationSchema(labelSettings, pensionEnrollmentProps, currentStep);

	return (
		<>
			<Formik
				initialValues={initialValues}
				onSubmit={(values, formikHelpers) => onSubmit(values, formikHelpers)}
				validationSchema={validationSchema}
				initialErrors={apiErrors}
				enableReinitialize
			>
				{(formProps: FormikProps<PensionEnrollmentFormValues>) => {
					return (
						<>
							<Form>
								<FormWrapper
									formikProps={formProps}
									cancelText={buttonLabels[0]}
									submitText={buttonLabels[1]}
									onCancel={() => cancelAction(formProps)}
									submitDisabled={submitDisabled(formProps)}
									hideCancelButton={!plansAvailable}
									preventButtonDisableValidation={preventButtonDisableValidation}
								>
									{renderFormContent(formProps)}
								</FormWrapper>
							</Form>
						</>
					);
				}}
			</Formik>
		</>
	);
}